import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { isApiError, isRetryable, type ApiError } from './http/errors';

/** 화면별로 전역 에러 토스트를 끄거나 문구를 바꾸는 meta */
export interface AppQueryMeta extends Record<string, unknown> {
  /** 화면이 직접 에러를 보여 줄 때 전역 토스트를 끈다(예: DataTable errorState). */
  suppressGlobalError?: boolean;
  /** 전역 토스트 문구 — 없으면 서버 메시지 */
  errorMessage?: string;
}

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: ApiError;
    queryMeta: AppQueryMeta;
    mutationMeta: AppQueryMeta;
  }
}

function notify(error: unknown, meta: AppQueryMeta | undefined) {
  // 취소는 사용자가 화면을 떠난 것이라 알리지 않는다. 인증 처리(onUnauthorized)가 맡은 401도 알리지 않는다 —
  // API 키 서버의 401이나 로그인 연동 전 401처럼 아무도 맡지 않은 401은 토스트로 알린다.
  if (
    meta?.suppressGlobalError ||
    (isApiError(error) && (error.kind === 'aborted' || error.authHandled))
  ) {
    return;
  }
  const message = meta?.errorMessage ?? (error instanceof Error ? error.message : undefined);
  // 같은 문구가 여러 요청에서 동시에 나도 토스트는 하나만 띄운다.
  toast.error(message ?? '요청을 처리하지 못했습니다.', { id: message });
}

/** 앱 전체에서 하나만 만든다 — 렌더마다 새로 만들면 캐시가 매번 사라진다. */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // 이미 화면에 데이터가 있는 상태의 백그라운드 갱신 실패만 토스트로 알린다 — 첫 로딩 실패는 화면이 보여 준다.
      if (query.state.data !== undefined) {
        notify(error, query.meta);
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      notify(error, mutation.meta);
    },
  }),
  defaultOptions: {
    queries: {
      // 어드민 데이터는 몇십 초 사이에 잘 바뀌지 않는다 — 화면 이동마다 다시 받지 않게 한다.
      staleTime: 30_000,
      // 4xx·설정 누락은 다시 보내도 같다. 네트워크·5xx만 두 번까지.
      retry: (failureCount, error) => failureCount < 2 && isRetryable(error),
    },
    mutations: {
      retry: false,
    },
  },
});

/** 계정/세션 경계에서 abort부터 실행한 후 이전 query·mutation 결과를 동기 제거한다. */
export function clearOperationalCache(client: QueryClient = queryClient): void {
  void client.cancelQueries();
  client.clear();
}

/** A stale domain denial clears only that domain's current-session views. */
export function clearOperationalDomainCache(
  domain: string,
  generation: number,
  apiNamespace: string,
  client: QueryClient = queryClient,
): void {
  const queryNames: Record<string, readonly string[]> = {
    property: [
      'properties',
      'property-catalog',
      'property-source-preview',
      'website-publication',
      'property-application-setting',
      'room-online-access',
      'access-device-inventory',
      'core-access-device-inventory',
      'access-device-registry',
      'access-device-command-context',
      'inventory-slot-occupancy',
      'property-media-groups',
      'property-media-items',
      'native-property-catalog',
      'native-properties',
      'native-access-inventory',
      'access-relations',
      'public-midterm-list',
      'public-midterm-property',
    ],
    booking: [
      'bookings',
      'booking-detail',
      'admin-stay',
      'admin-stays',
      'financial-reconciliation',
      'native-midterm-price',
      'native-type-pricing-edit',
      'native-room-pricing-edit',
      'native-midterm-sale',
    ],
    order: ['orders', 'order-detail', 'financial-reconciliation'],
    payment: ['payments', 'payment-detail', 'financial-reconciliation'],
    entitlement: ['stay-entitlements', 'access-targets'],
    inventory: ['inventory-slot-occupancy'],
    user: ['users', 'user-detail', 'user-authorization'],
  };
  const names = queryNames[domain];
  if (!names) return;
  const prefixes = names.map((name) => ['core', apiNamespace, generation, name]);
  void client.cancelQueries({
    predicate: (query) =>
      prefixes.some((key) => key.every((part, index) => query.queryKey[index] === part)),
  });
  client.removeQueries({
    predicate: (query) =>
      prefixes.some((key) => key.every((part, index) => query.queryKey[index] === part)),
  });
}
